import {
  PlanInfo,
  AllowedModelMode,
  DEPLOYMENT_TRACKS
} from '../types/plans';

/**
 * Returns a numeric rank for plans (higher = more access).
 */
function planRank(plan: string): number {
  const ranks: Record<string, number> = {
    free: 0,
    plus: 1,
    ultra: 2,
    pro: 3,
    business: 2,
    enterprise: 3,
  };
  return ranks[plan] ?? 0;
}

/**
 * Gets the display name for a plan combination.
 */
export function getPlanDisplayName(planInfo: PlanInfo): string {
  const track = DEPLOYMENT_TRACKS[planInfo.track];

  const planNames: Record<string, string> = {
    free: 'Free',
    plus: 'Plus',
    ultra: 'Ultra',
    pro: 'Pro',
    business: 'Business',
    enterprise: 'Enterprise',
    payment_required: 'Payment Required',
  };

  const planName = planNames[planInfo.plan] || planInfo.plan;

  if (planInfo.track === 'cloud') {
    return planName;
  } else if (track) {
    return `${track.name} (${planName})`;
  } else {
    return planName;
  }
}

/**
 * Checks if a plan is the free tier.
 */
export function isFreePlan(planInfo: PlanInfo): boolean {
  return planInfo.plan === 'free';
}

/**
 * Checks if a plan is Plus or above.
 */
export function isPlusOrAbove(planInfo: PlanInfo): boolean {
  return planRank(planInfo.plan) >= planRank('plus');
}

/**
 * Checks if a plan is Ultra or above ($36/mo).
 */
export function isUltraOrAbove(planInfo: PlanInfo): boolean {
  return planRank(planInfo.plan) >= planRank('ultra');
}

/**
 * Checks if a plan is Pro (top tier, $100/mo).
 */
export function isProPlan(planInfo: PlanInfo): boolean {
  return planRank(planInfo.plan) >= planRank('pro');
}

// ─── Model access ─────────────────────────────────────────────────────────────

/**
 * Returns whether the plan is allowed to use the given model mode.
 * - fastest: all plans
 * - expert:  plus and above
 * - genius:  ultra and above
 */
export function canUseModel(planInfo: PlanInfo, mode: AllowedModelMode): boolean {
  switch (mode) {
    case 'fastest':
      return true;
    case 'expert':
      return isPlusOrAbove(planInfo);
    case 'genius':
      return isUltraOrAbove(planInfo);
    default:
      return false;
  }
}

// ─── Feature flags ────────────────────────────────────────────────────────────

/**
 * Whether the plan can paste images in chat.
 * Free plan cannot; Plus and above can.
 */
export function canPasteImages(planInfo: PlanInfo): boolean {
  return isPlusOrAbove(planInfo);
}

/**
 * Whether the plan has access to integrations (Gmail, Drive, etc.).
 * Ultra and above only.
 */
export function canUseIntegrations(planInfo: PlanInfo): boolean {
  return isUltraOrAbove(planInfo);
}

/**
 * Whether the plan can generate images.
 * Free plan cannot; Plus and above have a monthly quota.
 */
export function canGenerateImages(planInfo: PlanInfo): boolean {
  return isPlusOrAbove(planInfo);
}

// ─── Limits ───────────────────────────────────────────────────────────────────

/**
 * Maximum number of projects (spaces) the plan allows.
 * Returns -1 for unlimited.
 */
export function getProjectLimit(planInfo: PlanInfo): number {
  const limits: Record<string, number> = {
    free: 1,
    plus: 5,
    ultra: -1,
    pro: -1,
  };
  return limits[planInfo.plan] ?? -1;
}

/**
 * Monthly image generation limit.
 * Returns -1 for unlimited; 0 means no access.
 */
export function getMonthlyImageGenLimit(planInfo: PlanInfo): number {
  const limits: Record<string, number> = {
    free: 0,
    plus: 25,
    ultra: 100,
    pro: 300,
  };
  return limits[planInfo.plan] ?? -1;
}

// ─── Upgrade helpers ──────────────────────────────────────────────────────────

export function isFounderPlan(planInfo: PlanInfo): boolean {
  return planInfo.plan.startsWith('founder-');
}

export function getNextUpgradePlan(currentPlan: PlanInfo): PlanInfo | null {
  switch (currentPlan.plan) {
    case 'free':
      return { ...currentPlan, plan: 'plus' };
    case 'plus':
      return { ...currentPlan, plan: 'ultra' };
    case 'ultra':
      return { ...currentPlan, plan: 'pro' };
    case 'pro':
    case 'enterprise':
      return null;
    default:
      if (isFounderPlan(currentPlan)) return null;
      return null;
  }
}

export function getUpgradeMessage(currentPlan: PlanInfo): string {
  const nextPlan = getNextUpgradePlan(currentPlan);
  if (!nextPlan) {
    if (isFounderPlan(currentPlan)) {
      return 'You\'re on a founder plan with unlimited access!';
    }
    return 'You\'re already on our highest plan!';
  }

  const planNames: Record<string, string> = {
    free: 'Free',
    plus: 'Plus',
    ultra: 'Ultra',
    pro: 'Pro',
    business: 'Business',
    enterprise: 'Enterprise',
  };

  const planName = planNames[nextPlan.plan] || nextPlan.plan;
  return `Upgrade to ${planName} to unlock more features and higher limits.`;
}

/**
 * Converts a raw billing plan key (as stored in the DB) to its user-facing display name.
 */
export function getBillingPlanDisplayName(plan: string): string {
  const planNames: Record<string, string> = {
    free: 'Free',
    plus: 'Plus',
    ultra: 'Ultra',
    pro: 'Pro',
    business: 'Business',
    enterprise: 'Enterprise',
  };
  return planNames[plan] || plan;
}

// Legacy compatibility
export const isFreemiumPlan = isFreePlan;

export interface LimitExceededData {
  error: 'LIMIT_EXCEEDED';
  upgrade_required: true;
  plan_type: string;
  limit_type: string;
  limits: Record<string, { used: number; max: number }>;
  message: string;
}

/**
 * Extracts a structured LIMIT_EXCEEDED payload from a fetch error, handling two cases:
 *  1. Direct 403: FastAPI wraps the HTTPException detail in `{"detail": {...}}`, so the
 *     payload lives at `error.data.detail` (not `error.data` directly).
 *  2. 500 wrapping a 403: the backend occasionally re-raises HTTPException as 500;
 *     in that case `error.data.detail` is a Python-formatted dict string like
 *     `"403: {'error': 'LIMIT_EXCEEDED', 'upgrade_required': True, ...}"`
 */
export function extractLimitExceededError(error: any): LimitExceededData | null {
  if (!error) return null;

  // Direct 403 — FastAPI returns {"detail": {...}} so unwrap one level
  if (error.status === 403) {
    // `detail` may be the nested object, or the data itself if the route returns it directly
    const payload = error.data?.detail ?? error.data;
    if (payload?.error === 'LIMIT_EXCEEDED') {
      return payload as LimitExceededData;
    }
  }

  // 500 wrapping a 403 — `error.data.detail` is a Python dict string
  if (error.status === 500) {
    const detail: string | undefined =
      typeof error.data?.detail === 'string' ? error.data.detail :
      typeof error.message === 'string' ? error.message : undefined;

    if (detail && detail.includes("'error': 'LIMIT_EXCEEDED'")) {
      try {
        // Convert Python dict to valid JSON
        const raw = detail.replace(/^.*?403:\s*/, '');
        const json = raw
          .replace(/'/g, '"')
          .replace(/\bTrue\b/g, 'true')
          .replace(/\bFalse\b/g, 'false')
          .replace(/\bNone\b/g, 'null');
        const parsed = JSON.parse(json);
        if (parsed?.error === 'LIMIT_EXCEEDED') return parsed as LimitExceededData;
      } catch {
        // Regex fallback when the string can't be cleanly converted to JSON
        const planType = detail.match(/'plan_type':\s*'(\w+)'/)?.[1] ?? 'free';
        const limitType = detail.match(/'limit_type':\s*'(\w+)'/)?.[1] ?? 'monthly_cost';
        const message = detail.match(/'message':\s*"([^"]+)"/)?.[1] ?? '';
        return {
          error: 'LIMIT_EXCEEDED',
          upgrade_required: true,
          plan_type: planType,
          limit_type: limitType,
          limits: {},
          message,
        };
      }
    }
  }

  return null;
}
